import React, { useEffect, useState } from 'react';
import { fetchCrops } from '../api/cropService';
import axiosInstance from '../api/axiosInstance';
import { getCurrentLanguage } from '../api/languageHelper';
import { useTranslation } from 'react-i18next';
import { getaccesstoken } from '../context/AuthContext';

const apiPrefix = process.env.REACT_APP_API_PREFIX;

const AddCrop = () => {
    const { t } = useTranslation();
    const [crops, setCrops] = useState([]);
    const [cropName, setCropName] = useState('');
    const [cropDescription, setCropDescription] = useState('');
    const [cropImage, setCropImage] = useState(null);
    const [loading, setLoading] = useState(false);

    // Array of symptom objects
    const [l1Symptoms, setL1Symptoms] = useState([
        { name: '', description: '', question: '' }
    ]);

    const AUTH_TOKEN = getaccesstoken(); // TODO: replace with valid token

    useEffect(() => {
        const loadCrops = async () => {
            try {
                const data = await fetchCrops(true);
                setCrops(data);
            } catch (error) {
                console.error('Error loading crops:', error);
            }
        };
        loadCrops();
    }, []);

    const addSymptom = () => {
        setL1Symptoms([...l1Symptoms, { name: '', description: '', question: '' }]);
    };

    const removeSymptom = (index) => {
        setL1Symptoms(l1Symptoms.filter((_, i) => i !== index));
    };

    const updateSymptom = (index, field, value) => {
        const updatedSymptoms = [...l1Symptoms];
        updatedSymptoms[index][field] = value;
        setL1Symptoms(updatedSymptoms);
    };

    const handleSubmit = async () => {
        if (!cropName || !cropDescription) {
            alert(t('expert.addCrop.alertFillDetails'));
            return;
        }

        setLoading(true);

        try {
            const locale = getCurrentLanguage();

            // Step 1: Create Crop
            const cropPayload = {
                name: cropName,
                imageUrl: cropImage ? cropImage.name : '',
                locale_code: locale,
                description: cropDescription,
            };

            const cropResponse = await axiosInstance.post(`${apiPrefix}crops`, cropPayload, {
                headers: {
                    'auth-token': AUTH_TOKEN
                }
            });

            const cid = cropResponse.data.cid;

            // Step 2: Add L1 Symptoms
            if (l1Symptoms.length > 0 && l1Symptoms.some(s => s.name.trim() !== '')) {
                const symptomsPayload = l1Symptoms.map(symptom => ({
                    level: 1,
                    cid: String(cid),
                    sid: undefined,
                    locale_code: locale,
                    name: symptom.name,
                    description: symptom.description,
                    qn: symptom.question
                }));

                const symptomResponse = await axiosInstance.post(`${apiPrefix}symptom/array/post`, symptomsPayload, {
                    headers: {
                        'auth-token': AUTH_TOKEN
                    }
                });
                console.log('L1 Symptoms added:', symptomResponse.data);
            }

            alert(t('expert.addCrop.alertSuccess'));
            // Reload crops list
            const data = await fetchCrops(true);
            setCrops(data);
        } catch (error) {
            alert(t('expert.addCrop.alertError'));
        } finally {
            setLoading(false);
            // Reset form
            setCropName('');
            setCropDescription('');
            setCropImage(null);
            setL1Symptoms([{ name: '', description: '', question: '' }]);
        }
    };

    // ------------------- UI -------------------
    return (
        <div className="flex h-screen">
            <>
                {/* LEFT: Crop Details */}
                <div className="w-2/3 p-6 overflow-y-auto bg-gray-50">
                    <div className="bg-white shadow rounded-lg p-6">
                        <h2 className="text-2xl font-semibold mb-6">{t('expert.addCrop.pageTitle')}</h2>

                        {/* Crop Name */}
                        <div className="mb-4">
                            <label className="block text-gray-700 mb-2">{t('expert.addCrop.cropNameLabel')}</label>
                            <input
                                type="text"
                                value={cropName}
                                onChange={(e) => setCropName(e.target.value)}
                                className="w-full px-3 py-2 border rounded-md focus:outline-blue-500"
                                placeholder={t('expert.addCrop.cropNamePlaceholder')}
                            />
                        </div>

                        {/* Crop Description */}
                        <div className="mb-4">
                            <label className="block text-gray-700 mb-2">{t('expert.addCrop.cropDescriptionLabel')}</label>
                            <textarea
                                value={cropDescription}
                                onChange={(e) => setCropDescription(e.target.value)}
                                className="w-full px-3 py-2 border rounded-md focus:outline-blue-500 h-24"
                                placeholder={t('expert.addCrop.cropDescriptionPlaceholder')}
                            />
                        </div>

                        {/* Crop Image */}
                        <div className="mb-4">
                            <label className="block text-gray-700 mb-2">{t('expert.addCrop.cropImageLabel')}</label>
                            <input
                                type="file"
                                onChange={(e) => setCropImage(e.target.files[0])}
                                className="block w-full text-gray-700 border border-gray-300 rounded-md cursor-pointer"
                            />
                        </div>

                        {/* Add L1 Symptoms Section */}
                        <h3 className="text-xl font-semibold my-6">{t('expert.addCrop.l1SymptomsTitle')}</h3>

                        {l1Symptoms.map((symptom, index) => (
                            <div key={index} className="mb-6 p-4 border rounded-md bg-gray-50">
                                <div className="mb-4">
                                    <label className="block text-gray-700 mb-2">{t('expert.addCrop.l1SymptomNameLabel')}</label>
                                    <input
                                        type="text"
                                        value={symptom.name}
                                        onChange={(e) => updateSymptom(index, 'name', e.target.value)}
                                        className="w-full px-3 py-2 border rounded-md focus:outline-blue-500"
                                    />
                                </div>

                                <div className="mb-4">
                                    <label className="block text-gray-700 mb-2">{t('expert.addCrop.l1SymptomDescriptionLabel')}</label>
                                    <textarea
                                        value={symptom.description}
                                        onChange={(e) => updateSymptom(index, 'description', e.target.value)}
                                        className="w-full px-3 py-2 border rounded-md focus:outline-blue-500 h-24"
                                    />
                                </div>

                                <div className="mb-4">
                                    <label className="block text-gray-700 mb-2">{t('expert.addCrop.l1SymptomQuestionLabel')}</label>
                                    <input
                                        type="text"
                                        value={symptom.question}
                                        onChange={(e) => updateSymptom(index, 'question', e.target.value)}
                                        className="w-full px-3 py-2 border rounded-md focus:outline-blue-500"
                                    />
                                </div>

                                {index !== 0 && (
                                    <div className="flex justify-end">
                                        <button
                                            type="button"
                                            onClick={() => removeSymptom(index)}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            {t('expert.addCrop.removeButton')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}

                        <div className="mb-6">
                            <button
                                type="button"
                                onClick={addSymptom}
                                className="text-blue-500"
                            >
                                {t('expert.addCrop.addAnotherSymptom')}
                            </button>
                        </div>

                        {/* Submit Button */}
                        <div className="mt-6 flex justify-center">
                            <button
                                onClick={handleSubmit}
                                disabled={loading}
                                className={`bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 transition ${loading ? 'opacity-50' : ''}`}
                            >
                                {loading ? t('expert.addCrop.submittingButton') : t('expert.addCrop.submitButton')}
                            </button>
                        </div>
                    </div>
                </div>

                {/* RIGHT: Crop List */}
                <div className="w-1/3 p-6 border-l border-gray-200 overflow-y-auto ">
                    <h2 className="text-2xl font-bold mb-4">{t('expert.addCrop.existingCropsTitle')}</h2>
                    <ul>
                        {crops.map(crop => (
                            <li key={crop.cid} className="p-3 rounded mb-2">
                                {crop.name}
                            </li>
                        ))}
                    </ul>
                </div>
            </>
        </div>
    );
};

export default AddCrop;